import { useTranslation } from 'react-i18next';
import { Link, NavLink, Outlet } from 'react-router-dom';
import { BrandLogo } from '../../shared/components/BrandLogo';
import { Icon, type IconName } from '../../shared/components/Icon';
import { LanguageSelect } from '../../shared/components/LanguageSelect';
import { PoweredBy } from '../../shared/components/ui';
import { useTheme } from '../../shared/theme/theme';
import { useAuth } from '../auth/AuthContext';

const portalNavigation: readonly { to: string; labelKey: string; icon: IconName; end?: boolean; invitationPermission?: boolean }[] = [
  { to: '/portal', labelKey: 'portal.overview', icon: 'dashboard', end: true },
  { to: '/portal/documents', labelKey: 'portal.documents', icon: 'journal' },
  { to: '/portal/workforce', labelKey: 'portal.workforce', icon: 'payroll' },
  { to: '/portal/identity', labelKey: 'portal.identity', icon: 'user' },
  { to: '/invitations', labelKey: 'invitations.title', icon: 'send', invitationPermission: true },
];

/**
 * The light shell a Varza Inter-organization Identity holder gets. It is deliberately not the console: there is no
 * ledger, no data entry, and no tenant scope — only the documents businesses have issued to this person and
 * the card that identifies them.
 */
export function PortalShell() {
  const { t } = useTranslation();
  const { userLabel, logout, session, tenants } = useAuth();
  const { theme, toggle: toggleTheme } = useTheme();

  return (
    <div className="portal">
      <a className="skip-link" href="#portal-content">{t('app.skipToContent')}</a>

      <header className="portal__bar">
        <Link className="portal__brand" to="/">
          <span className="brand__mark" aria-hidden="true"><BrandLogo /></span>
          <span className="brand__copy">
            <strong>{t('app.name')}</strong>
            <small>{t('app.tagline')}</small>
          </span>
        </Link>

        <nav className="portal__nav" aria-label={t('portal.name')}>
          {portalNavigation.filter((item) => !item.invitationPermission || session?.canCreateInvitations).map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end ?? false}
              className={({ isActive }) => `portal__nav-link${isActive ? ' portal__nav-link--active' : ''}`}
            >
              <Icon name={item.icon} />
              <span>{t(item.labelKey)}</span>
            </NavLink>
          ))}
        </nav>

        <div className="portal__actions">
          <LanguageSelect />
          <button
            className="theme-switch"
            type="button"
            onClick={toggleTheme}
            aria-label={theme === 'dark' ? t('theme.switchToLight') : t('theme.switchToDark')}
          >
            <Icon name={theme === 'dark' ? 'sun' : 'moon'} />
          </button>
          {/* Someone who also belongs to a business keeps one way back into the full workspace. */}
          {tenants.length > 0 && (
            <Link className="button button--secondary button--compact" to="/console">
              <Icon name="dashboard" />{t('landing.openWorkspace')}
            </Link>
          )}
          <details className="popover">
            <summary className="user-summary" aria-label={t('context.userMenu')}>
              <span className="avatar" aria-hidden="true">{userLabel.slice(0, 1).toLocaleUpperCase()}</span>
            </summary>
            <div className="popover__panel popover__panel--end account-menu">
              <div className="account-menu__identity"><strong>{userLabel}</strong></div>
              <button type="button" onClick={() => void logout()}>
                <Icon name="arrow" className="icon directional-icon" />{t('auth.logout')}
              </button>
            </div>
          </details>
        </div>
      </header>

      <main id="portal-content" className="portal__main" tabIndex={-1}>
        <Outlet />
      </main>

      <footer className="portal__footer">
        <PoweredBy />
      </footer>
    </div>
  );
}
